import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AppBarCMS } from '../../../../../basic/index';
import { CollapseBlock, Spinner } from '../../../../../common/index';
import ErasmusApplicationForm from './ErasmusApplicationForm/ErasmusApplicationForm.jsx';
import {
  useGetErasmusApplicationsQuery,
  useGetErasmusApplicationQuery,
  useSaveErasmusApplicationMutation,
  useDeleteErasmusApplicationMutation,
} from '../../../../../../redux/services/erasmusApplications';
import { routes } from '../../../../../../utils/helpers/ApiClient';
import dayjs from 'dayjs';
import { useGetUsersByIdsQuery, useGetUsersQuery } from '../../../../../../redux/services/users';
import useIsUserAdmin from '../../../../../../hooks/useIsUserAdmin';
import useIsUserErasmusKoordinator from '../../../../../../hooks/useIsUserErasmusKoordinator';
import { useToastMessages } from '../../../../../../hooks/useToastMessages.jsx';
import Form from '../../../../../antd/form/Form/Form.jsx';
import FormSelect from '../../../../../antd/form/FormSelect/FormSelect.jsx';
import FormButtonsContainer from '../../../../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import FormButton from '../../../../../antd/form/FormButton/FormButton.jsx';
import { required } from '../../../../../antd/form/FormValidation.js';
import { Avatar, Button, Col, Row, Space, Typography } from 'antd';
import { UserOutlined } from '@ant-design/icons';

const ErasmusApplicationEdit = ({ mode, id }) => {
  const { id: urlId } = useParams();
  const displayId = id || urlId;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAdmin = useIsUserAdmin() || useIsUserErasmusKoordinator();
  const isErasmusKoordinator = useIsUserErasmusKoordinator();
  const [selectedStudentId, setSelectedStudentId] = useState();

  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const isPreview = mode === 'preview';
  const user = useSelector((reduxStore) => reduxStore.auth.user);
  const shouldChooseStudent = isCreate && isErasmusKoordinator;
  const createUserId = shouldChooseStudent ? selectedStudentId : user.person_id;

  const { data: createUserData, isLoading: isCreateUserLoading } = useGetUsersByIdsQuery([createUserId], {
    skip: !isCreate || !createUserId,
  });
  const { data: erasmusStudentUsers, isLoading: isStudentOptionsLoading } = useGetUsersQuery(
    {
      type: 'forErasmusStudentChooser',
    },
    {
      skip: !shouldChooseStudent,
    },
  );

  const studentOptions = useMemo(
    () =>
      erasmusStudentUsers?.map((item) => ({
        value: item.person_id,
        label: item.full_name,
      })) ?? [],
    [erasmusStudentUsers],
  );

  const selectedCreateUser = createUserData?.[0];

  useEffect(() => {
    if (!shouldChooseStudent) {
      setSelectedStudentId(undefined);
    }
  }, [shouldChooseStudent]);

  useEffect(() => {
    if (
      shouldChooseStudent &&
      selectedStudentId &&
      studentOptions.length > 0 &&
      !studentOptions.some((option) => option.value === selectedStudentId)
    ) {
      setSelectedStudentId(undefined);
    }
  }, [shouldChooseStudent, selectedStudentId, studentOptions]);

  useEffect(() => {
    if (
      shouldChooseStudent &&
      !selectedStudentId &&
      studentOptions.length === 1
    ) {
      setSelectedStudentId(studentOptions[0].value);
    }
  }, [shouldChooseStudent, selectedStudentId, studentOptions]);

  const fallbackCreateUser = {
    id: createUserId,
    person_id: createUserId,
    first_name: shouldChooseStudent ? undefined : user.first_name,
    last_name: shouldChooseStudent ? undefined : user.last_name,
    login: shouldChooseStudent ? undefined : user.login,
    email: shouldChooseStudent ? undefined : user.mail,
    mail: shouldChooseStudent ? undefined : user.mail,
    students: [],
  };

  const createInitialUser = selectedCreateUser ?? fallbackCreateUser;

  const createInitials = {
    person_id: shouldChooseStudent ? createUserId : undefined,
    first_name: createInitialUser.first_name,
    last_name: createInitialUser.last_name,
    uid: createInitialUser.login,
    mobility_type: undefined,
    mobility_subtyp: undefined,
    birth_date: dayjs(),
    address: undefined,
    phone: undefined,
    email: createInitialUser.mail ?? createInitialUser.email,
    photo_path: undefined,
    language_certificate_path: undefined,
    average_year1_bc: undefined,
    average_year2_bc: undefined,
    average_year3_bc: undefined,
    average_year1_ing: undefined,
    average_year2_ing: undefined,
    study_program: undefined,
    study_language: 'en',
    expected_study_year: undefined,
    receives_social_scholarship: false,
    university1_id: undefined,
    university2_id: undefined,
    university3_id: undefined,
    mobility_year: dayjs(),
    mobility_semester: undefined,
    previous_mobility_id: undefined,
    submission_date: undefined,
    cv_path: undefined,
    motivation_letter_path: undefined,
    created_at: undefined,
    updated_at: undefined,
    tzp_path: undefined,
    social_scholarship_path: undefined,
    tzp: false,
    applicationStatus: 'Rozpracovaná',
    person: createInitialUser,
  };

  const isWaitingForStudentSelection =
    shouldChooseStudent && !selectedStudentId;

  const isWaitingForSelectedStudentData =
    shouldChooseStudent && !!selectedStudentId && isCreateUserLoading;

  const showCreateForm =
    !shouldChooseStudent || (!!selectedStudentId && !!selectedCreateUser);

  const selectedStudentLabel =
    studentOptions.find((option) => option.value === selectedStudentId)?.label;

  const renderStudentChooser = () => (
    <div style={{ margin: '10px' }}>
      <Row>
        <Col md={{ span: 16, offset: 4 }} sm={{ span: 22, offset: 1 }}>
          <CollapseBlock title={t('Výber študenta')} collapsible="false">
            <Form
              initialValues={{ person_id: selectedStudentId }}
              onFinish={(values) => {
                setSelectedStudentId(values.person_id);
              }}
            >
              <FormSelect
                label={t('Študent')}
                name={'person_id'}
                placeholder={t('Vyhľadať študenta')}
                options={studentOptions}
                rules={[required(t)]}
                showSearch
                loading={isStudentOptionsLoading}
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />

              <FormButtonsContainer>
                <FormButton submitOnClick={true}>
                  {t('Pokračovať')}
                </FormButton>
              </FormButtonsContainer>
            </Form>
          </CollapseBlock>
        </Col>
      </Row>
    </div>
  );

  const {
    data: applicationDetail,
    isLoading,
    isError: isErrorApplication,
  } = useGetErasmusApplicationQuery(displayId, {
    skip: !isEdit && !isPreview,
  });
  const personEdit = applicationDetail?.application?.person;

  const { saveSuccess } = useToastMessages();
  useDeleteErasmusApplicationMutation();
  const [saveErasmusApplications, { isSuccess: isSaveSuccess }] =
    useSaveErasmusApplicationMutation();

  const { refetch } = useGetErasmusApplicationsQuery();

  useEffect(() => {
    if (isSaveSuccess) {
      saveSuccess();
      navigate(routes.cmsErasmusApplication);
      refetch();
    }
  }, [isSaveSuccess]);

  function getYear(value) {
    // Ak je to dayjs objekt
    if (dayjs.isDayjs(value)) {
      return value.year();
    }

    // Ak je to obyčajné číslo (napr. 2024)
    if (typeof value === 'number') {
      return value;
    }

    // Ak je to string (napr. "2024" alebo "2024-01-01")
    if (typeof value === 'string') {
      // ak je to len rok
      if (/^\d{4}$/.test(value)) {
        return parseInt(value, 10);
      }
      // inak skúsi parsovať ako dátum
      return dayjs(value).year();
    }

    return null; // fallback
  }

  const title = (isCreate && t('Pridanie prihlášky')) || t('Úprava prihlášky');
  // || (isEdit && !userEdit)
  // Primárny loader
  if (isLoading) {
    return <Spinner />;
  }

  // Ak pri načítaní prihlášky nastala chyba, nezasekni spinner a zobraz jednoduchú správu
  if ((isEdit || isPreview) && isErrorApplication) {
    return (
      <>
        {!isPreview ? (
          <AppBarCMS
            title={title}
            backUrl={routes.cmsErasmusApplication}
            backUrlTooltip={t('Prihlášky')}
            permissionsBasic={'erasmus-applications'}
          />
        ) : undefined}
        <div style={{ padding: '16px' }}>
          {t('Nepodarilo sa načítať prihlášku.')}
        </div>
      </>
    );
  }

  // V edit/preview móde: ak nie je loading a data neprišli, zobraz informáciu namiesto nekonečného spinnera
  if ((isEdit || isPreview) && !applicationDetail) {
    return (
      <>
        {!isPreview ? (
          <AppBarCMS
            title={title}
            backUrl={routes.cmsErasmusApplication}
            backUrlTooltip={t('Prihlášky')}
            permissionsBasic={'erasmus-applications'}
          />
        ) : undefined}
        <div style={{ padding: '16px' }}>
          {t('Prihláška nebola nájdená alebo nie je dostupná.')}
        </div>
      </>
    );
  }

  // V create móde: nikdy neblokuj spinnerom pre chýbajúce createUserData, použijeme fallback zo session
  const initials =
    isEdit || isPreview
      ? {
          ...applicationDetail.application,
          birth_date: dayjs(applicationDetail?.application?.birth_date),
          mobility_year: applicationDetail?.application?.mobility_year
            ? dayjs(`${applicationDetail.application.mobility_year}-01-01`) // Create a full date from year
            : undefined, // Or whatever default you want
          first_name: personEdit.first_name,
          last_name: personEdit.last_name,
          averages: {
            1: {
              average: applicationDetail.application.average_year1_bc ?? null,
            },
            2: {
              average: applicationDetail.application.average_year2_bc ?? null,
            },
            3: {
              average: applicationDetail.application.average_year3_bc ?? null,
            },
            4: {
              average: applicationDetail.application.average_year1_ing ?? null,
            },
            5: {
              average: applicationDetail.application.average_year2_ing ?? null,
            },
          },
        }
      : createInitials;

  if (!initials) {
    return <Spinner />;
  }

  if (isCreate && (isWaitingForSelectedStudentData || (!showCreateForm && !isWaitingForStudentSelection))) {
    return <Spinner />;
  }

  const phonePrefixes = [
    '+421', '+420', '+48', '+36', '+43', '+49', '+385', '+386', '+387', '+389',
    '+380', '+31', '+32', '+33', '+34', '+39', '+41', '+44',
  ];

  const splitPhone = (phone) => {
    if (!phone) {
      return { phone_prefix: '+421', phone_number: '' };
    }
    const trimmed = String(phone).trim();
    const match = phonePrefixes.find((p) => trimmed.startsWith(p));
    if (match) {
      const rest = trimmed.slice(match.length).trim();
      return { phone_prefix: match, phone_number: rest };
    }
    const fallbackMatch = trimmed.match(/^(\+?\d{1,4})\s*(.*)$/);
    if (fallbackMatch) {
      const prefix = fallbackMatch[1].startsWith('+') ? fallbackMatch[1] : `+${fallbackMatch[1]}`;
      return { phone_prefix: prefix, phone_number: (fallbackMatch[2] || '').trim() };
    }
    return { phone_prefix: '+421', phone_number: trimmed };
  };

  const phoneParts = splitPhone(initials.phone);

  return (
    <>
      {!isPreview ? (
        <AppBarCMS
          title={title}
          backUrl={routes.cmsErasmusApplication}
          backUrlTooltip={t('Prihlášky')}
          permissionsBasic={'erasmus-applications'}
        />
      ) : undefined}
      {isWaitingForStudentSelection ? renderStudentChooser() : undefined}
      {showCreateForm ? (
      <div style={{ margin: '10px' }}>
        {shouldChooseStudent ? (
          <div
            style={{
              marginBottom: '16px',
              border: '1px solid #f0f0f0',
              borderRadius: '12px',
              padding: '12px 16px',
              background: '#fafafa',
            }}
          >
            <Row align="middle" justify="space-between" gutter={[16, 16]}>
              <Col flex="auto">
                <Space size={12} align="center">
                  <Avatar
                    size={44}
                    icon={<UserOutlined />}
                    style={{ backgroundColor: '#e6f4ff', color: '#1677ff' }}
                  />
                  <div>
                    <Typography.Text type="secondary">
                      {t('Vybraný študent')}
                    </Typography.Text>
                    <br />
                    <Typography.Text strong style={{ fontSize: '16px' }}>
                      {selectedStudentLabel}
                    </Typography.Text>
                  </div>
                </Space>
              </Col>
              <Col>
                <Button
                  type="primary"
                  onClick={() => {
                    setSelectedStudentId(undefined);
                  }}
                >
                  {t('Zmeniť študenta')}
                </Button>
              </Col>
            </Row>
          </div>
        ) : undefined}
        <ErasmusApplicationForm
          key={isCreate ? (createUserId ?? 'create') : (displayId ?? mode)}
          initials={{
            ...initials,
            phone_prefix: phoneParts.phone_prefix,
            phone_number: phoneParts.phone_number,
          }}
          isEdit={isEdit}
          isPreview={isPreview}
          onSubmit={(values) => {
            const { phone_prefix, phone_number, ...restValues } = values;
            const cleanPrefix = String(phone_prefix || '').trim();
            const cleanNumber = String(phone_number || '').trim();
            const combinedPhone =
              cleanPrefix && cleanNumber
                ? `${cleanPrefix} ${cleanNumber}`
                : (cleanNumber || cleanPrefix || null);

            const body = {
              ...restValues,
              phone: combinedPhone,
              social_scholarship_path: values.social_scholarship_path ?? initials.social_scholarship_path,
              tzp_path: values.tzp_path ?? initials.tzp_path,
              mobility_year: getYear(values.mobility_year),
            };

            saveErasmusApplications({ body: body, id: displayId });
          }}
          isAdmin={isAdmin}
        />
      </div>
      ) : undefined}
    </>
  );
};

export default ErasmusApplicationEdit;
